import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { getFriendlyError } from '../utils/friendlyErrors';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast({
        title: 'Invalid link',
        description: 'This reset link is invalid. Please request a new one.',
        variant: 'destructive'
      });
      navigate('/login');
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

      const response = await fetch(`${BACKEND_URL}/api/auth/verify-reset-token/${token}`);

      if (!response.ok) {
        // iter-230m — defensive parse: a 502/504 HTML body would throw
        // inside response.json() and surface the "JSON.parse" friendly
        // message; we treat any non-JSON failure as an expired link.
        let detail = null;
        try {
          const body = await response.json();
          detail = body?.detail;
        } catch (_e) {
          detail = null;
        }
        const message =
          (typeof detail === 'string' && detail) ||
          (detail && typeof detail === 'object' && detail.message) ||
          'This reset link has expired. Please request a new one.';
        throw new Error(message);
      }

      const data = await response.json();
      setTokenValid(true);
      setUserEmail(data.email);
    } catch (error) {
      toast({
        title: 'Invalid reset link',
        description: getFriendlyError(error, 'This reset link is invalid or has expired. Please request a new one.'),
        variant: 'destructive'
      });
      setTokenValid(false);
      setTimeout(() => navigate('/login'), 3000);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast({
        title: 'Missing fields',
        description: 'Please fill in both password fields.',
        variant: 'destructive'
      });
      return;
    }

    // iter-230m — align FE with backend PASSWORD_POLICY (min 10 chars +
    // uppercase + lowercase + digit + special). Previous check was a
    // permissive `length < 6` so vendors typing weak passwords got
    // through the FE then hit a generic backend error.
    if (password.length < 10) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 10 characters long.',
        variant: 'destructive'
      });
      return;
    }
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*()\-_=+\[\]{}|;:'",.<>?/`~]/.test(password);
    if (!hasUpper || !hasLower || !hasDigit || !hasSpecial) {
      toast({
        title: 'Password too weak',
        description: 'Password must include uppercase, lowercase, number, and special character.',
        variant: 'destructive'
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords don\'t match',
        description: 'Please make sure both passwords are the same.',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          new_password: password
        })
      });

      if (!response.ok) {
        // iter-230m — robust error parsing. Backend now returns
        // `{code, message, errors?}` as detail; old paths return a
        // plain string. Handle both + non-JSON bodies (HTML 5xx).
        let detail = null;
        try {
          const body = await response.json();
          detail = body?.detail;
        } catch (_e) {
          detail = null;
        }
        let message;
        let code = null;
        if (typeof detail === 'string') {
          message = detail;
        } else if (detail && typeof detail === 'object') {
          code = detail.code || null;
          message = detail.message || 'Could not reset password. Please try again.';
          if (code === 'PASSWORD_WEAK' && Array.isArray(detail.errors) && detail.errors.length) {
            message = `${message} ${detail.errors.join(' ')}`;
          }
        } else {
          message = `Reset failed (${response.status}). Please try again.`;
        }

        // Token-state errors: bounce user back to forgot-password page
        if (code === 'TOKEN_INVALID' || code === 'TOKEN_EXPIRED' || code === 'TOKEN_USED') {
          toast({
            title: 'Reset link unusable',
            description: message,
            variant: 'destructive'
          });
          setTimeout(() => navigate('/forgot-password'), 2000);
          return;
        }

        throw new Error(message);
      }

      setSuccess(true);
      toast({
        title: 'Password reset',
        description: 'Your password has been reset successfully.',
      });

      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast({
        title: 'Unable to reset password',
        description: getFriendlyError(error),
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
              <p className="text-gray-600">Verifying reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <AlertCircle className="h-8 w-8 text-red-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">Invalid Reset Link</CardTitle>
            <CardDescription className="text-center">
              This password reset link is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              onClick={() => navigate('/forgot-password')}
              className="w-full"
            >
              Request New Reset Link
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
        <Card className="w-full max-w-md shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-center">Password Reset Successful</CardTitle>
            <CardDescription className="text-center">
              Your password has been reset successfully
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 text-center mb-4">
              You can now login with your new password
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="space-y-1">
          <CardTitle className="text-3xl font-bold text-center">Reset Password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password for {userEmail}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  data-testid="reset-password-new-input"
                />
              </div>
              <p className="text-xs text-gray-500">At least 10 characters with uppercase, lowercase, number &amp; special character</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  data-testid="reset-password-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              data-testid="reset-password-submit-btn"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
